import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useUnsavedGuard, clearUnsavedWork } from "../../lib/unsaved";
import { Button, Field, PhoneField, Select, ErrorNote, Panel } from "../../components/ui";
import { loadCounties, surtaxLabel, type County } from "../../lib/counties";
import AddressField from "./AddressField";
import { US_STATES, COUNTRIES, isUsState, isCountry } from "../../lib/places";
import {
  NameField, TextField, StateSelect, EinField, EmailField, DateOfBirthField, LicenseNumberField, ExpiryField } from "../../components/fields";

/** What the number is called, so an error never asks a passport holder for
    a license number. */
const ID_LABELS: Record<string, string> = {
  drivers_license: "License number",
  state_id: "State ID number",
  passport: "Passport number",
  military_id: "Military ID number",
};
const ID_LABEL_FOR = (k: string) => ID_LABELS[k] ?? "Number";
import type { AddressParts } from "../../lib/addressLookup";
import { countyForZip } from "../../lib/zipCounty";
import {
  checkRequired, checkPhone, checkEmail, checkZip, checkDateOfBirth,
  stripEin, checkEin,
} from "../../lib/validation";
import { SaveBar } from "../../components/save";
import { mustSave, useSaveState } from "../../lib/saving";

type Errs = Record<string, string | null>;

const BLANK = {
  firstName: "", middleName: "", lastName: "", suffix: "", dateOfBirth: "",
  idType: "drivers_license", idNumber: "", idState: "FL", idExpiration: "",
  street: "", city: "", county: "", state: "FL", zip: "",
  mailSame: true, mailStreet: "", mailCity: "", mailState: "FL", mailZip: "",
  phoneCell: "", phoneHome: "", email: "",
  isBusiness: false, businessName: "", businessEin: "", businessRegState: "FL",
  businessTitle: "",
};

export default function AddCustomer({ onDone, onCancel }: {
  onDone: (id: string) => void; onCancel: () => void;
}) {
  const [f, setF] = useState({ ...BLANK });

  /* Saved and on its way out. Without this the guard fires on the one
     navigation that is supposed to happen: the form is still full at the
     moment it saves, so leaving looked like abandoning work. */
  const [saved, setSaved] = useState(false);

  /* Anything typed but not yet saved. Compared against the blank form rather
     than tracked with a flag, so clearing a field back to empty counts as
     clean again. */
  useUnsavedGuard(
    !saved && Object.keys(BLANK).some((k) =>
      (f as Record<string, unknown>)[k] !== (BLANK as Record<string, unknown>)[k]),
    "this customer");
  const [errs, setErrs] = useState<Errs>({});
  const save_ = useSaveState();
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [counties, setCounties] = useState<County[]>([]);
  const [countyHint, setCountyHint] = useState<string | null>(null);
  const [countyTouched, setCountyTouched] = useState(false);

  useEffect(() => {
    loadCounties().then(setCounties).catch((e) => setLoadErr(e.message));
  }, []);

  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const touch = (k: string, m: string | null) => setErrs((e) => ({ ...e, [k]: m }));

  /**
   * The county sets the discretionary surtax, and scrolling 67 options is a
   * chore. A ZIP fills it in, but never overwrites a county chosen by hand,
   * and an unrecognised ZIP leaves it blank rather than guessing.
   */
  function zipChanged(v: string) {
    setF((s) => {
      const next = { ...s, zip: v };
      if (s.state.toUpperCase() !== "FL" || countyTouched) return next;
      const hit = countyForZip(v);
      if (hit) {
        next.county = hit.county;
        setCountyHint(hit.exact ? `Filled from the ZIP.` : `Best guess from the ZIP — check it.`);
        touch("county", null);
      } else {
        setCountyHint(null);
      }
      return next;
    });
  }

  function countyChanged(v: string) {
    setCountyTouched(true);
    setCountyHint(null);
    set("county")(v);
  }

  function validateAll(): boolean {
    const next: Errs = {
      firstName: checkRequired(f.firstName, "First name"),
      lastName: checkRequired(f.lastName, "Last name"),
      /* Only when a business is buying — a person has none of these, and
         the database refuses a half-filled business anyway. */
      businessName: f.isBusiness
        ? checkRequired(f.businessName, "Business name") : null,
      businessEin: f.isBusiness ? checkEin(f.businessEin) : null,
      businessTitle: f.isBusiness
        ? checkRequired(f.businessTitle, "Signer's title") : null,
      dateOfBirth: checkDateOfBirth(f.dateOfBirth),
      idNumber: checkRequired(f.idNumber, ID_LABEL_FOR(f.idType))
        ?? null,
      idState: checkRequired(f.idState,
        f.idType === "passport" ? "Issuing country" : "Issuing state"),
      street: checkRequired(f.street, "Street address"),
      city: checkRequired(f.city, "City"),
      zip: checkZip(f.zip),
      phoneCell: checkPhone(f.phoneCell),
      phoneHome: checkPhone(f.phoneHome),
      email: checkEmail(f.email),
      county: f.state.toUpperCase() === "FL" && !f.county
        ? "Pick a county — it sets the surtax." : null,
      contact: !f.phoneCell && !f.phoneHome && !f.email
        ? "Add a phone number or an email." : null,
    };
    if (!f.mailSame) {
      next.mailStreet = checkRequired(f.mailStreet, "Mailing street");
      next.mailCity = checkRequired(f.mailCity, "Mailing city");
      next.mailZip = checkZip(f.mailZip);
    }
    setErrs(next);
    return Object.values(next).every((v) => v === null);
  }

  async function save() {
    if (!validateAll()) { save_.fail("Fix the fields marked in red, then press Save again."); return; }
    let newId = "";
    const ok = await save_.run(async () => {
      const { data, error } = await supabase.from("customers").insert({
        first_name: f.firstName.trim(),
        middle_name: f.middleName.trim() || null,
        last_name: f.lastName.trim(),
        suffix: f.suffix.trim() || null,
        date_of_birth: f.dateOfBirth,
        id_type: f.idType,
        id_number: f.idNumber.trim(),
        id_state: f.idState.toUpperCase(),
        is_business: f.isBusiness,
        business_name: f.isBusiness ? f.businessName.trim() : null,
        business_ein: f.isBusiness ? stripEin(f.businessEin) : null,
        business_reg_state: f.isBusiness ? f.businessRegState : null,
        business_title: f.isBusiness ? f.businessTitle.trim() : null,
        id_expiration: f.idExpiration || null,
        street: f.street.trim(),
        city: f.city.trim(),
        county: f.county || null,
        state: f.state.toUpperCase(),
        zip: f.zip.trim(),
        mail_same_as_physical: f.mailSame,
        mail_street: f.mailSame ? null : f.mailStreet.trim(),
        mail_city: f.mailSame ? null : f.mailCity.trim(),
        mail_state: f.mailSame ? null : f.mailState.toUpperCase(),
        mail_zip: f.mailSame ? null : f.mailZip.trim(),
        phone_cell: f.phoneCell || null,
        phone_home: f.phoneHome || null,
        email: f.email.trim() || null,
      }).select("id").single();
      newId = (mustSave({ data, error }) as { id: string }).id;
    }, { toast: "Customer saved" });
    if (!ok) return;
    setSaved(true);
    clearUnsavedWork();
    onDone(newId);
  }

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>Add a customer</h2>
          <p className="pagehead-sub">
            Names exactly as they appear on the driver license.
          </p>
        </div>
      </div>

      {/* A business buys the car; a person signs for it. Both are recorded,
          because the 82040 wants the company in the owner field and a human
          in the signature block. */}
      <Panel title="Who is buying" rail="record">
        <Select label="Buyer type"
                value={f.isBusiness ? "business" : "person"}
                onChange={(v) => setF((s) => ({ ...s, isBusiness: v === "business" }))}>
          <option value="person">A person</option>
          <option value="business">A business</option>
        </Select>

        {f.isBusiness && (
          <>
            <TextField label="Business name" required value={f.businessName}
                       onChange={set("businessName")} error={errs.businessName}
                       hint="Exactly as it is registered" />
            <div className="grid2">
              <EinField label="EIN" required value={f.businessEin}
                        onChange={set("businessEin")} error={errs.businessEin} />
              <Select label="State of registration" value={f.businessRegState}
                      onChange={set("businessRegState")}>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </Select>
            </div>
            <TextField label="Signer's title" required value={f.businessTitle}
                       onChange={set("businessTitle")} error={errs.businessTitle}
                       hint="President, Managing Member, and so on" />
            <p className="hint dim">
              The person below signs their own name, with that title printed
              beside it — which is what makes the signature bind the company.
              Their license still goes on the paperwork.
            </p>
          </>
        )}
      </Panel>

      <Panel title={f.isBusiness ? "Owner or officer" : "Customer information"}
             rail="record">
      {/* First, middle, last, in the order they are read off the license.
          First and last on one row with middle underneath meant jumping back
          up the form for the middle name on every customer. */}
      <div className="grid2">
        <NameField label="First name" required value={f.firstName}
                   onChange={set("firstName")} error={errs.firstName} />
        <NameField label="Middle name" value={f.middleName}
                   onChange={set("middleName")} />
      </div>

      <div className="grid2">
        <NameField label="Last name" required value={f.lastName}
                   onChange={set("lastName")} error={errs.lastName} />
        <TextField label="Suffix" value={f.suffix} onChange={set("suffix")}
                   hint="Jr, III, and so on" />
      </div>

      <DateOfBirthField required value={f.dateOfBirth}
                        onChange={set("dateOfBirth")} error={errs.dateOfBirth} />

      </Panel>

      <Panel title="Identification" rail="record"
             note="printed on the title application">
      {/* Changing the type clears the issuing place. A country code left in
          a state dropdown matches no option, so the box read "Choose" while
          still holding "VE" — and Florida license formatting, which only
          runs when the state is FL, quietly stopped working. */}
      <Select label="ID type" value={f.idType}
              onChange={(v) => setF((s) => ({
                ...s,
                idType: v,
                idState: v === "passport"
                  ? (isCountry(s.idState) ? s.idState : "")
                  : (isUsState(s.idState) ? s.idState : "FL"),
              }))}>
        <option value="drivers_license">Driver license</option>
        <option value="state_id">State ID</option>
        <option value="passport">Passport</option>
      </Select>
      <div className="grid2">
        <LicenseNumberField label={ID_LABEL_FOR(f.idType)} required value={f.idNumber}
                   onChange={set("idNumber")} idType={f.idType} idState={f.idState}
                   error={errs.idNumber} />
        {/* A passport has no issuing state — it has an issuing country. The
            form used to ask for a state regardless, which left a two-letter
            box that could hold anything. */}
        {f.idType === "passport" ? (
          <Select label="Issuing country" value={f.idState} onChange={set("idState")}>
            <option value="">Choose</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </Select>
        ) : (
          <Select label="Issuing state" value={f.idState} onChange={set("idState")}>
            <option value="">Choose</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </Select>
        )}
      </div>
      <ExpiryField value={f.idExpiration} onChange={set("idExpiration")} />

      </Panel>

      <Panel title="Address" rail="record">
      <AddressField
        value={f.street} onChange={set("street")} error={errs.street}
        onBlur={() => touch("street", checkRequired(f.street, "Street address"))}
        onPick={(a: AddressParts) => setF((s) => ({
          ...s,
          city: a.city || s.city,
          state: a.state || s.state,
          zip: a.zip || s.zip,
          // Only if it is a county we actually have a tax rate for.
          county: counties.some((c) => c.name === a.county) ? a.county : s.county,
        }))} />
      <div className="grid2">
        <Field label="City" value={f.city} onChange={set("city")} error={errs.city}
               onBlur={() => touch("city", checkRequired(f.city, "City"))} />
        {/* Its own handler: typing a ZIP also sets the county, which decides
            the surtax. The validation is the shared one. */}
        <Field label="ZIP" inputMode="numeric" maxLength={10} value={f.zip}
               onChange={zipChanged} error={errs.zip}
               onBlur={() => touch("zip", checkZip(f.zip))} />
      </div>
      <div className="grid2">
        <StateSelect label="State" required value={f.state} onChange={set("state")} />
        <Select label="County" value={f.county} onChange={countyChanged}
                placeholder="Choose" error={errs.county}
                hint={countyHint ?? undefined}>
          {counties.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name} ({surtaxLabel(c.surtax_bps)})
            </option>
          ))}
        </Select>
      </div>

      <label className="checkline">
        <input type="checkbox" checked={f.mailSame}
               onChange={(e) => setF((s) => ({ ...s, mailSame: e.target.checked }))} />
        Mail goes to this address
      </label>

      {!f.mailSame && (
        <>
          <Field label="Mailing street" value={f.mailStreet} onChange={set("mailStreet")}
                 error={errs.mailStreet} />
          <div className="grid2">
            <Field label="Mailing city" value={f.mailCity} onChange={set("mailCity")}
                   error={errs.mailCity} />
            <Field label="Mailing ZIP" inputMode="numeric" value={f.mailZip}
                   onChange={set("mailZip")} error={errs.mailZip} />
          </div>
        </>
      )}

      </Panel>

      <Panel title="Contact" rail="record">
      <div className="grid2">
        <PhoneField label="Cell" value={f.phoneCell} onChange={set("phoneCell")}
                    error={errs.phoneCell} />
        <PhoneField label="Home" value={f.phoneHome} onChange={set("phoneHome")}
                    error={errs.phoneHome} />
      </div>
      <EmailField value={f.email} onChange={set("email")} error={errs.email} />
      {errs.contact && <p className="msg">{errs.contact}</p>}
      </Panel>

      {loadErr && <ErrorNote>{loadErr}</ErrorNote>}
      <SaveBar save={save_} onSave={() => void save()} label="Save customer"
               alwaysPrimary idle="Not saved yet">
        <Button variant="ghost" onClick={onCancel} disabled={save_.saving}>Cancel</Button>
      </SaveBar>
    </>
  );
}
